import { useCallback, useEffect, useRef, useState } from "react";
import { getCase } from "@/features/cases/api";
import type { Case } from "@/features/cases/types";
import { ApiError } from "@/lib/api";

interface UseCaseResult {
  caseRecord: Case | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  /** Replace the cached case (after an edit/complete/step change) without refetch. */
  setCase: (c: Case) => void;
  refetch: () => void;
}

/**
 * Fetches a single case by id. Same request-guarding pattern as `use-cases.ts`
 * (collapses StrictMode's double-invoke, drops out-of-order responses).
 */
export function useCase(caseId: string): UseCaseResult {
  const [caseRecord, setCaseRecord] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${caseId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setNotFound(false);

    getCase(caseId)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setCaseRecord(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setCaseRecord(null);
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError(err instanceof ApiError ? err.message : "Failed to load case.");
        }
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [caseId, reloadKey]);

  return { caseRecord, loading, error, notFound, setCase: setCaseRecord, refetch };
}
